export interface ColunaGrid {
	header: string
	field: string
	numerico: boolean
	moeda: boolean
	aggFunc?: string | null
	grupo?: string
}

export interface SecaoPdf {
	titulo: string
	headers: ColunaGrid[]
	itens: any[]
}

export interface RelatorioGridParams {
	corPrimaria: string
	empresa: string
	geradoEm: string
	titulo: string
	filtros: { label: string; valor: string }[]
	colunas: ColunaGrid[]
	agrupadores: { field: string; header: string }[]
	linhas: any[]
	orientacao?: 'retrato' | 'paisagem'
	mostrarSubtotais?: boolean
	mostrarTotalGeral?: boolean
}

export interface PivotValor {
	field: string
	header: string
	moeda: boolean
}

export interface PivotLinha {
	field: string
	header: string
}

export interface PivotConfig {
	colunaField: string
	linhas?: PivotLinha[]
	valores?: PivotValor[]
	// legado (template antigo com 1 linha / 1 valor):
	linhaField?: string
	linhaHeader?: string
	valorField?: string
	valorHeader?: string
	moeda?: boolean
}

/**
 * Transforma linhas planas em tabela cruzada (pivot):
 * - linhas agrupadas por `linhaField`
 * - uma coluna por (valor distinto de `colunaField` × métrica)
 * - célula = soma da métrica
 * - colunas de Total por métrica (omitidas quando há um único valor pivotado)
 * Retorna { colunas, linhas } no mesmo formato consumido por gerarRelatorioGrid.
 */
export function montarPivot(linhas: any[], cfg: PivotConfig): { colunas: ColunaGrid[]; linhas: any[] } {
	const metricas: PivotValor[] = (cfg.valores && cfg.valores.length)
		? cfg.valores
		: (cfg.valorField ? [{ field: cfg.valorField, header: cfg.valorHeader || cfg.valorField, moeda: !!cfg.moeda }] : [])
	if (!metricas.length) return { colunas: [], linhas: [] }
	const multi = metricas.length > 1

	const dims: PivotLinha[] = (cfg.linhas && cfg.linhas.length)
		? cfg.linhas
		: (cfg.linhaField ? [{ field: cfg.linhaField, header: cfg.linhaHeader || cfg.linhaField }] : [])
	if (!dims.length) return { colunas: [], linhas: [] }

	const valores: string[] = []
	const idxValor = new Map<string, number>()
	linhas.forEach(r => {
		const v = String(r[cfg.colunaField] ?? '—')
		if (!idxValor.has(v)) { idxValor.set(v, valores.length); valores.push(v) }
	})
	valores.sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
	valores.forEach((v, i) => idxValor.set(v, i))

	const campoCelula = (i: number, m: PivotValor) => `p${i}_${m.field}`
	const campoTotal = (m: PivotValor) => `tot_${m.field}`

	const SEP = ''
	const grupos = new Map<string, any>()
	linhas.forEach(r => {
		const chaves = dims.map(d => String(r[d.field] ?? '—'))
		const k = chaves.join(SEP)
		let g = grupos.get(k)
		if (!g) {
			g = {}
			dims.forEach((d, i) => { g['__l' + i] = chaves[i] })
			valores.forEach((v, i) => metricas.forEach(m => { g[campoCelula(i, m)] = 0 }))
			metricas.forEach(m => { g[campoTotal(m)] = 0 })
			grupos.set(k, g)
		}
		const cv = String(r[cfg.colunaField] ?? '—')
		const vi = idxValor.get(cv)
		metricas.forEach(m => {
			const n = Number(r[m.field])
			const num = isNaN(n) ? 0 : n
			if (vi !== undefined) g[campoCelula(vi, m)] += num
			g[campoTotal(m)] += num
		})
	})

	const linhasOut = Array.from(grupos.values()).sort((a, b) => {
		for (let i = 0; i < dims.length; i++) {
			const r = String(a['__l' + i]).localeCompare(String(b['__l' + i]), 'pt-BR', { numeric: true })
			if (r !== 0) return r
		}
		return 0
	})

	const colunas: ColunaGrid[] = dims.map((d, i) => ({ header: d.header, field: '__l' + i, numerico: false, moeda: false }))
	valores.forEach((v, i) => metricas.forEach(m => {
		colunas.push({ header: m.header, field: campoCelula(i, m), numerico: true, moeda: m.moeda, aggFunc: 'sum', grupo: v })
	}))
	// Com um único valor pivotado (ex.: uma loja só) o Total repetiria a mesma coluna.
	if (valores.length !== 1) {
		metricas.forEach(m => {
			colunas.push({ header: m.header, field: campoTotal(m), numerico: true, moeda: m.moeda, aggFunc: 'sum', grupo: 'Total' })
		})
	}
	return { colunas, linhas: linhasOut }
}

// ===========================================================================
// Blocos de grade (1 cartão por produto) — alternativa ao pivot global único.
// Em vez de juntar TODOS os tamanhos de TODOS os produtos numa só tabela (que
// fica gigante e cheia de células vazias), cada produto vira um bloco com um
// pivot LOCAL: só as colunas de tamanho que aquele produto realmente possui.
// Inspirado no relatório de grade do MACLE.
// ===========================================================================
export interface BlocoGradeConfig {
	idFields: { field: string; header: string }[]   // identidade/título do bloco (código, descrição)
	colunaField: string                              // campo pivotado (tamanho)
	metricas: PivotValor[]                           // métricas -> viram as linhas (Compras/Vendas/Estoque)
	imagemField?: string | null                      // campo de miniatura (opcional)
}

export interface BlocoGrade {
	titulo: string
	imagem: string | null
	tamanhos: string[]
	linhas: { header: string; celulas: (number | null)[]; total: number; moeda: boolean }[]
	totais: { header: string; total: number; moeda: boolean }[]
}

function ehUrlImagem(v: any): boolean {
	if (typeof v !== 'string') return false
	const s = v.trim()
	return /^https?:\/\//i.test(s) || /^data:image\//i.test(s)
}

// Converte valores que podem vir como número OU string mal-formatada/decimal BR.
// Ex.: "1.000" -> 1 (decimal), "1,5" -> 1.5, "1.234,50" -> 1234.5, 7 -> 7.
// NÃO divide nada: apenas interpreta o separador decimal/milhar corretamente.
export function tratarNumero(v: any): number {
	if (v === null || v === undefined || v === '') return 0
	if (typeof v === 'number') return isNaN(v) ? 0 : v
	let s = String(v).trim()
	if (!s) return 0
	s = s.replace(/[^\d.,-]/g, '')           // remove R$, espaços, etc.
	if (!s || s === '-' || s === '.' || s === ',') return 0
	const temPonto = s.includes('.')
	const temVirgula = s.includes(',')
	if (temPonto && temVirgula) {
		// o último separador que aparece é o decimal; o outro é milhar
		if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.')
		else s = s.replace(/,/g, '')
	} else if (temVirgula) {
		s = s.replace(/\./g, '').replace(',', '.')   // vírgula = decimal BR
	} else if (temPonto) {
		// só pontos: vários pontos = milhar; um único ponto = decimal ("1.000" -> 1)
		if ((s.match(/\./g) || []).length > 1) s = s.replace(/\./g, '')
	}
	const n = Number(s)
	return isNaN(n) ? 0 : n
}

// Ordem canônica de tamanhos de roupa/calçado. Letras seguem PP<P<M<G<GG<XG...;
// numéricos (35, 36, "39/40") ordenam pelo número; o resto (UN, ÚNICO) vai ao fim.
const ORDEM_TAMANHOS = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XGG', 'XXG', 'EXG', 'G1', 'G2', 'G3', 'G4', 'G5', 'G6']
function rankTamanho(t: string): { grupo: number; chave: number; txt: string } {
	const s = String(t ?? '').trim().toUpperCase()
	const i = ORDEM_TAMANHOS.indexOf(s)
	if (i >= 0) return { grupo: 0, chave: i, txt: s }
	const m = s.match(/\d+([.,]\d+)?/)               // primeiro número (ex.: "39/40" -> 39)
	if (m) return { grupo: 1, chave: parseFloat(m[0].replace(',', '.')), txt: s }
	return { grupo: 2, chave: 0, txt: s }
}
export function compararTamanhos(a: string, b: string): number {
	const ra = rankTamanho(a); const rb = rankTamanho(b)
	if (ra.grupo !== rb.grupo) return ra.grupo - rb.grupo
	if (ra.chave !== rb.chave) return ra.chave - rb.chave
	return ra.txt.localeCompare(rb.txt, 'pt-BR', { numeric: true })
}

/**
 * Agrupa as linhas planas por produto (idFields) e, para CADA grupo, monta um
 * pivot local: tamanhos do próprio produto como colunas e uma linha por métrica.
 * Função pura — consumida tanto pelo PDF quanto pela pré-visualização do builder.
 */
export function montarBlocosDeGrade(linhas: any[], cfg: BlocoGradeConfig): BlocoGrade[] {
	const metricas = cfg.metricas || []
	const idFields = cfg.idFields || []
	if (!metricas.length || !idFields.length || !cfg.colunaField) return []

	const grupos = new Map<string, any[]>()
	linhas.forEach(r => {
		const k = idFields.map(f => String(r[f.field] ?? '')).join('||')
		if (!grupos.has(k)) grupos.set(k, [])
		grupos.get(k)!.push(r)
	})

	const blocos: BlocoGrade[] = []
	grupos.forEach(rows => {
		const r0 = rows[0]
		const titulo = idFields
			.filter(f => f.field !== cfg.imagemField)
			.map(f => String(r0[f.field] ?? '').trim())
			.filter(Boolean)
			.join(' - ')
		const imagem = cfg.imagemField && ehUrlImagem(r0[cfg.imagemField]) ? String(r0[cfg.imagemField]).trim() : null

		const setT = new Set<string>()
		rows.forEach(r => setT.add(String(r[cfg.colunaField] ?? '—')))
		const tamanhos = Array.from(setT).sort(compararTamanhos)
		const idxT = new Map(tamanhos.map((t, i) => [t, i]))

		const linhasMetrica = metricas.map(m => {
			const celulas = tamanhos.map(() => 0)
			let total = 0
			rows.forEach(r => {
				const num = tratarNumero(r[m.field])
				const ti = idxT.get(String(r[cfg.colunaField] ?? '—'))
				if (ti !== undefined) celulas[ti] += num
				total += num
			})
			// célula vazia (0) fica em branco, como no MACLE
			return { header: m.header, celulas: celulas.map(c => (c ? c : null)), total, moeda: !!m.moeda }
		})
		const totais = linhasMetrica.map(l => ({ header: l.header, total: l.total, moeda: l.moeda }))
		blocos.push({ titulo, imagem, tamanhos, linhas: linhasMetrica, totais })
	})

	blocos.sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR', { numeric: true }))
	return blocos
}

export interface RelatorioBlocosParams {
	corPrimaria: string
	empresa: string
	geradoEm: string
	titulo: string
	filtros: { label: string; valor: string }[]
	linhas: any[]
	bloco: BlocoGradeConfig
	orientacao?: 'retrato' | 'paisagem'
}

function fmtNum(v: number, moeda: boolean): string {
	if (moeda) return 'R$ ' + v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
	return v.toLocaleString('pt-BR')
}

export function gerarRelatorioBlocosDeGrade(p: RelatorioBlocosParams) {
	const cor = p.corPrimaria || '#60269E'
	const corClara = clarear(cor, 0.9)
	const gradiente = `linear-gradient(135deg, ${escurecer(cor, 0.5)} 0%, ${cor} 100%)`

	const blocos = montarBlocosDeGrade(p.linhas || [], p.bloco)

	const blocoHtml = (b: BlocoGrade) => {
		const thsTam = b.tamanhos.map(t => `<th>${esc(t)}</th>`).join('')
		const corpo = b.linhas.map(l => {
			const tds = l.celulas.map(c => `<td>${c == null ? '' : fmtNum(c, l.moeda)}</td>`).join('')
			return `<tr><td class="g-lbl">${esc(l.header)}</td>${tds}<td class="g-tot">${fmtNum(l.total, l.moeda)}</td></tr>`
		}).join('')
		const kpis = b.totais.map(t =>
			`<div class="kpi"><span class="kpi-h">${esc(t.header)}</span><span class="kpi-v">${fmtNum(t.total, t.moeda)}</span></div>`).join('')
		const img = b.imagem
			? `<img class="b-img" src="${esc(b.imagem)}" alt="" loading="lazy">`
			: `<div class="b-img b-img-ph"></div>`
		return `<div class="bloco">
			${img}
			<div class="b-main">
				<div class="b-ttl">${esc(b.titulo) || '—'}</div>
				<table class="grade">
					<thead><tr><th class="g-lbl">Tam</th>${thsTam}<th class="g-tot">TOT</th></tr></thead>
					<tbody>${corpo}</tbody>
				</table>
			</div>
			<div class="b-kpis">${kpis}</div>
		</div>`
	}

	const filtrosHtml = p.filtros.filter(f => f.valor).map(f =>
		`<span class="topo-chip"><b>${esc(f.label)}</b> ${esc(f.valor)}</span>`).join('')
	const corpoBlocos = blocos.length
		? blocos.map(blocoHtml).join('')
		: `<div class="vazio">Sem dados para montar os blocos.</div>`

	const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>${esc(p.titulo)} — ${esc(p.empresa)}</title>
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
	* { margin:0; padding:0; box-sizing:border-box; }
	body { font-family:'Poppins',system-ui,sans-serif; color:#1d1c2b; background:#faf9fc; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
	.print-bar { text-align:center; padding:18px; }
	.print-btn { background:${cor}; color:#fff; border:none; border-radius:10px; padding:11px 26px; font-family:inherit; font-size:14px; font-weight:600; cursor:pointer; }
	.topo { background:${gradiente}; color:#fff; padding:24px 36px; display:flex; flex-direction:column; gap:14px; }
	.topo-top { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; }
	.topo .over { color:rgba(255,255,255,.7); font-size:11px; font-weight:600; letter-spacing:4px; }
	.topo .ttl { font-size:24px; font-weight:700; line-height:1.1; margin-top:2px; }
	.topo-r { text-align:right; font-size:13px; font-weight:500; white-space:nowrap; }
	.topo-r .dim { color:rgba(255,255,255,.7); font-size:11px; font-weight:400; margin-top:3px; }
	.topo-params { display:flex; flex-wrap:wrap; gap:8px; }
	.topo-chip { background:rgba(255,255,255,.16); color:#fff; font-size:11px; padding:5px 12px; border-radius:20px; }
	.topo-chip b { font-weight:600; margin-right:4px; }
	.wrap { padding:24px 36px 36px; }
	.blocos { display:grid; grid-template-columns:repeat(auto-fill, minmax(460px, 1fr)); gap:14px; }
	.bloco { display:flex; gap:12px; align-items:flex-start; background:#fff; border:1px solid #ece9f3; border-radius:12px; padding:12px 14px; break-inside:avoid; }
	.b-img { width:60px; height:60px; object-fit:contain; border-radius:6px; flex-shrink:0; background:#f6f5fb; }
	.b-img-ph { background:#f1eff7; }
	.b-main { flex:1; min-width:0; }
	.b-ttl { font-size:12px; font-weight:600; color:${escurecer(cor, 0.2)}; margin-bottom:7px; line-height:1.2; }
	table.grade { border-collapse:collapse; }
	table.grade th, table.grade td { border:1px solid #e9e6f1; font-size:10px; padding:3px 7px; text-align:center; white-space:nowrap; min-width:26px; }
	table.grade thead th { background:${cor}; color:#fff; font-weight:600; }
	table.grade th.g-lbl, table.grade td.g-lbl { text-align:left; font-weight:600; background:${corClara}; color:${escurecer(cor, 0.2)}; }
	table.grade thead th.g-lbl { background:${cor}; color:#fff; }
	table.grade .g-tot { font-weight:700; background:#faf9fc; }
	table.grade thead th.g-tot { background:${escurecer(cor, 0.15)}; color:#fff; }
	.b-kpis { display:flex; flex-direction:column; gap:5px; flex-shrink:0; }
	.kpi { background:#faf9fc; border:1px solid #ece9f3; border-radius:8px; padding:4px 10px; text-align:right; min-width:70px; }
	.kpi-h { display:block; font-size:8px; text-transform:uppercase; letter-spacing:.4px; color:#8a86a0; }
	.kpi-v { display:block; font-size:13px; font-weight:700; color:${escurecer(cor, 0.2)}; }
	.vazio { color:#9a96aa; font-size:13px; text-align:center; padding:40px 0; }
	.foot { text-align:center; color:#a7a3b8; font-size:11px; margin-top:22px; }
	@media print {
		.print-bar { display:none; }
		body { background:#fff; }
		.bloco { break-inside:avoid; }
		@page { size:A4 ${p.orientacao === 'retrato' ? 'portrait' : 'landscape'}; margin:12mm 10mm; }
	}
</style></head>
<body>
	<div class="print-bar"><button class="print-btn" onclick="window.print()">🖨 Imprimir / Salvar PDF</button></div>
	<div class="topo">
		<div class="topo-top">
			<div>
				<div class="over">RELATÓRIO</div>
				<div class="ttl">${esc(p.titulo)}</div>
			</div>
			<div class="topo-r">
				<div>${esc(p.empresa)}</div>
				<div class="dim">Gerado em ${esc(p.geradoEm)}</div>
			</div>
		</div>
		${filtrosHtml ? `<div class="topo-params">${filtrosHtml}</div>` : ''}
	</div>
	<div class="wrap">
		<div class="blocos">${corpoBlocos}</div>
		<div class="foot">${blocos.length} ${blocos.length === 1 ? 'produto' : 'produtos'} · Gerado em ${esc(p.geradoEm)}</div>
	</div>
</body></html>`

	const win = window.open('', '_blank')
	if (!win) return
	win.document.open()
	win.document.write(html)
	win.document.close()
}

function clarear(hex: string, fator: number): string {
	const h = hex.replace('#', '')
	if (h.length !== 6) return hex
	const n = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
	const mix = (c: number) => Math.round(c + (255 - c) * fator)
	return `#${n.map(c => mix(c).toString(16).padStart(2, '0')).join('')}`
}

function escurecer(hex: string, fator: number): string {
	const h = hex.replace('#', '')
	if (h.length !== 6) return '#2a1a4a'
	const n = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
	const mix = (c: number) => Math.round(c * (1 - fator))
	return `#${n.map(c => mix(c).toString(16).padStart(2, '0')).join('')}`
}

function esc(v: any): string {
	return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function fmtVal(v: any, col: ColunaGrid): string {
	if (v === null || v === undefined || v === '') return ''
	if (col.moeda) {
		const n = Number(v)
		return isNaN(n) ? esc(v) : 'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
	}
	if (col.numerico) {
		const n = Number(v)
		return isNaN(n) ? esc(v) : n.toLocaleString('pt-BR')
	}
	return esc(v)
}

function ehImagemUrl(v: any, col: ColunaGrid): boolean {
	if (typeof v !== 'string') return false
	const s = v.trim()
	if (!/^https?:\/\//i.test(s) && !/^data:image\//i.test(s)) return false
	const header = (col.header || '').toLowerCase()
	const field = (col.field || '').toLowerCase()
	const dica = /(imagem|image|foto|img|logo|thumb|avatar)/.test(header + ' ' + field)
	const ext = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(s)
	return dica || ext
}

function celData(v: any, col: ColunaGrid): string {
	if (ehImagemUrl(v, col)) return `<img class="cel-img" src="${esc(v)}" alt="" loading="lazy">`
	return fmtVal(v, col)
}

function agregar(linhas: any[], col: ColunaGrid): number | null {
	if (!col.aggFunc) return null
	const nums = linhas.map(l => Number(l[col.field])).filter(n => !isNaN(n))
	switch (col.aggFunc) {
		case 'sum': return nums.reduce((a, b) => a + b, 0)
		case 'avg': return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0
		case 'min': return nums.length ? Math.min(...nums) : 0
		case 'max': return nums.length ? Math.max(...nums) : 0
		case 'count': return linhas.length
		default: return null
	}
}

export function colunaDoRotuloDeTotal(colunas: { aggFunc?: string | null }[]): number {
	return colunas.findIndex(c => !c.aggFunc)
}

function linhaTotal(linhas: any[], colunas: ColunaGrid[], rotulo: string, classe: string): string {
	const iRotulo = colunaDoRotuloDeTotal(colunas)
	const tds = colunas.map((c, i) => {
		if (i === iRotulo) return `<td class="${classe} lbl">${esc(rotulo)}</td>`
		const agg = agregar(linhas, c)
		const txt = agg === null ? '' : (c.aggFunc === 'count' ? String(agg) : fmtVal(agg, c))
		if (iRotulo < 0 && i === 0) return `<td class="${classe} r">${esc(rotulo)}: ${txt}</td>`
		return `<td class="${classe} ${c.numerico ? 'r' : ''}">${txt}</td>`
	})
	return `<tr>${tds.join('')}</tr>`
}

export function gerarRelatorioGrid(p: RelatorioGridParams) {
	const cor = p.corPrimaria || '#60269E'
	const corClara = clarear(cor, 0.9)
	const gradiente = `linear-gradient(135deg, ${escurecer(cor, 0.5)} 0%, ${cor} 100%)`

	const temGrupos = p.colunas.some(c => c.grupo)
	let thead: string
	if (!temGrupos) {
		thead = `<tr>${p.colunas.map(c => `<th class="${c.numerico ? 'r' : ''}">${esc(c.header)}</th>`).join('')}</tr>`
	} else {
		const grupos: { nome: string | null; cols: ColunaGrid[] }[] = []
		p.colunas.forEach(c => {
			const g = c.grupo || null
			const last = grupos[grupos.length - 1]
			if (last && last.nome === g && g !== null) last.cols.push(c)
			else grupos.push({ nome: g, cols: [c] })
		})
		let r1 = ''
		let r2 = ''
		grupos.forEach(gr => {
			if (gr.nome === null) {
				gr.cols.forEach(c => { r1 += `<th class="${c.numerico ? 'r' : ''}" rowspan="2">${esc(c.header)}</th>` })
			} else {
				r1 += `<th class="grp-top" colspan="${gr.cols.length}">${esc(gr.nome)}</th>`
				gr.cols.forEach(c => { r2 += `<th class="${c.numerico ? 'r' : ''}">${esc(c.header)}</th>` })
			}
		})
		thead = `<tr>${r1}</tr><tr>${r2}</tr>`
	}

	const linhaDados = (l: any) => `<tr>${p.colunas.map(c => `<td class="${c.numerico ? 'r' : ''}">${celData(l[c.field], c)}</td>`).join('')}</tr>`
	const agrupadores = p.agrupadores || []
	const buildGrupos = (rows: any[], lvl: number): string => {
		if (lvl >= agrupadores.length) return rows.map(linhaDados).join('')
		const g = agrupadores[lvl]
		const map = new Map<string, any[]>()
		rows.forEach(r => { const k = String(r[g.field] ?? '—'); if (!map.has(k)) map.set(k, []); map.get(k)!.push(r) })
		let html = ''
		map.forEach((itens, chave) => {
			html += `<tr class="grp"><td colspan="${p.colunas.length}" style="padding-left:${10 + lvl * 16}px">${esc(g.header)}: ${esc(chave)} <span class="cnt">(${itens.length})</span></td></tr>`
			html += buildGrupos(itens, lvl + 1)
			if (p.mostrarSubtotais !== false) html += linhaTotal(itens, p.colunas, 'Subtotal', 'sub')
		})
		return html
	}
	const tbody = agrupadores.length ? buildGrupos(p.linhas, 0) : p.linhas.map(linhaDados).join('')
	const temTotais = p.colunas.some(c => c.aggFunc)
	const totalGeral = (temTotais && p.mostrarTotalGeral !== false) ? linhaTotal(p.linhas, p.colunas, 'Total geral', 'tot') : ''

	const filtrosHtml = p.filtros.filter(f => f.valor).map(f =>
		`<span class="topo-chip"><b>${esc(f.label)}</b> ${esc(f.valor)}</span>`).join('')

	const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>${esc(p.titulo)} — ${esc(p.empresa)}</title>
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
	* { margin:0; padding:0; box-sizing:border-box; }
	body { font-family:'Poppins',system-ui,sans-serif; color:#1d1c2b; background:#faf9fc; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
	.print-bar { text-align:center; padding:18px; }
	.print-btn { background:${cor}; color:#fff; border:none; border-radius:10px; padding:11px 26px; font-family:inherit; font-size:14px; font-weight:600; cursor:pointer; }
	.topo { background:${gradiente}; color:#fff; padding:24px 36px; display:flex; flex-direction:column; gap:14px; }
	.topo-top { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; }
	.topo .over { color:rgba(255,255,255,.7); font-size:11px; font-weight:600; letter-spacing:4px; }
	.topo .ttl { font-size:24px; font-weight:700; line-height:1.1; margin-top:2px; }
	.topo-r { text-align:right; font-size:13px; font-weight:500; white-space:nowrap; }
	.topo-r .dim { color:rgba(255,255,255,.7); font-size:11px; font-weight:400; margin-top:3px; }
	.topo-params { display:flex; flex-wrap:wrap; gap:8px; }
	.topo-chip { background:rgba(255,255,255,.16); color:#fff; font-size:11px; padding:5px 12px; border-radius:20px; }
	.topo-chip b { font-weight:600; margin-right:4px; }
	.wrap { padding:24px 36px 36px; }
	.card { background:#fff; border:1px solid #ece9f3; border-radius:14px; overflow:hidden; }
	/* Scroll horizontal na lateral quando há muitas colunas (na tela; na impressão fica visível). */
	.tbl-scroll { overflow-x:auto; }
	.tbl-scroll::-webkit-scrollbar { height:10px; }
	.tbl-scroll::-webkit-scrollbar-thumb { background:#cfc9dd; border-radius:6px; }
	.tbl-scroll::-webkit-scrollbar-track { background:#f1eff7; }
	table { width:100%; border-collapse:collapse; }
	th { background:${cor}; color:#fff; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.4px; padding:9px 11px; text-align:left; white-space:nowrap; }
	td { font-size:11px; padding:7px 11px; border-bottom:1px solid #f1eff7; white-space:nowrap; }
	td .cel-img, img.cel-img { max-height:46px; max-width:90px; object-fit:contain; border-radius:4px; display:block; }
	th.r, td.r { text-align:right; }
	th.grp-top { text-align:center; border-left:1px solid rgba(255,255,255,.28); }
	tr.grp td { background:${corClara}; color:${cor}; font-weight:600; font-size:11px; padding:8px 11px; }
	tr.grp .cnt { color:#9a96aa; font-weight:400; }
	td.sub { background:#faf9fc; font-weight:600; border-top:1px solid #e4e1ee; }
	td.tot { background:${cor}; color:#fff; font-weight:700; }
	td.lbl { text-transform:uppercase; font-size:10px; letter-spacing:.5px; }
	.foot { text-align:center; color:#a7a3b8; font-size:11px; margin-top:22px; }
	@media print {
		.print-bar { display:none; }
		body { background:#fff; }
		thead { display:table-header-group; }
		tr.grp, tr.sub, tr { break-inside:avoid; }
		.tbl-scroll { overflow:visible; }
		@page { size:A4 ${p.orientacao === 'retrato' ? 'portrait' : 'landscape'}; margin:12mm 10mm; }
	}
</style></head>
<body>
	<div class="print-bar"><button class="print-btn" onclick="window.print()">🖨 Imprimir / Salvar PDF</button></div>
	<div class="topo">
		<div class="topo-top">
			<div>
				<div class="over">RELATÓRIO</div>
				<div class="ttl">${esc(p.titulo)}</div>
			</div>
			<div class="topo-r">
				<div>${esc(p.empresa)}</div>
				<div class="dim">Gerado em ${esc(p.geradoEm)}</div>
			</div>
		</div>
		${filtrosHtml ? `<div class="topo-params">${filtrosHtml}</div>` : ''}
	</div>
	<div class="wrap">
		<div class="card">
			<div class="tbl-scroll">
				<table>
					<thead>${thead}</thead>
					<tbody>${tbody}${totalGeral}</tbody>
				</table>
			</div>
		</div>
		<div class="foot">Gerado em ${esc(p.geradoEm)}</div>
	</div>
</body></html>`

	const win = window.open('', '_blank')
	if (!win) return
	win.document.open()
	win.document.write(html)
	win.document.close()
}
